'use client';
import { Show, useUser } from '@clerk/nextjs';
import Link from 'next/link';
import cn from 'classnames';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@rocket-house-productions/shadcn-ui';

import { buttonVariants } from '@rocket-house-productions/shadcn-ui/server';
import { Menu, SettingsIcon } from 'lucide-react';
import React, { useState } from 'react';
import Image, { type StaticImageData } from 'next/image';
import { PrismicNextLink } from '@prismicio/next';

import { usePathname } from 'next/navigation';
import { asText } from '@prismicio/client';
import { UserSignedInDropdown } from '@rocket-house-productions/features';
import { type NavigationDocumentData } from '@/prismic-types';

export function NavLogin({
  navigation,
  logo,
}: {
  isAdmin?: boolean;
  navigation: NavigationDocumentData;
  logo: StaticImageData;
}) {
  const [open, setOpen] = useState(false);
  const currentRoute = usePathname();
  const { user } = useUser();

  const isAdmin = user?.publicMetadata.role === 'admin';

  return (
    <>
      <div className="other-options hidden self-center pb-[10px] pt-[20px] md:block xl:ml-[20px] xl:pb-[0] xl:pt-[0] 2xl:ml-[15px]">
        <ul className={'flex flex-row items-center justify-center space-x-2'}>
          <li className="flex items-center justify-center">
            <Show when="signed-out">
              <Link href="/sign-in" className={cn(buttonVariants({ variant: 'link' }), '!text-[16px] font-medium')}>
                Log in
              </Link>
            </Show>
            <Show when="signed-in">
              <UserSignedInDropdown />
            </Show>
          </li>
          <li>
            <Show when="signed-out">
              <Link
                href="/sign-up"
                className={cn(buttonVariants({ variant: 'default', size: 'sm' }), '!text-[14px] uppercase')}>
                Buy now
              </Link>
            </Show>
            <Show when="signed-in">
              <div className={'flex space-x-1'}>
                <Link href="/refresh" className={buttonVariants({ variant: 'outline' })}>
                  Go to Course
                </Link>
                {isAdmin && (
                  <Link href="/admin" className={buttonVariants({ variant: 'default' })}>
                    <i>
                      <SettingsIcon className={'h-4 w-4'} />{' '}
                    </i>
                    Admin
                  </Link>
                )}
              </div>
            </Show>
          </li>
        </ul>
      </div>

      <div className={'grid grow-0 grid-cols-2 items-center justify-center md:hidden'}>
        <div className={'mr-1.5 mt-1.5'}></div>

        {/* Toggle button */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger
            aria-controls="mobile-menu"
            className={'absolute right-0 top-1/2 z-50 block -translate-y-1/2 items-center md:hidden'}>
            <i className="bx bx-menu">
              <Menu />
            </i>
          </SheetTrigger>
          <SheetContent id="mobile-menu" side={'right'} className={'z[999] w-[80vw] px-0 pb-0'}>
            <SheetHeader>
              <SheetTitle className={'sr-only'}>Menu</SheetTitle>
            </SheetHeader>
            <div className={'flex h-full flex-col items-stretch'}>
              <div className={'grow'}>
                <Link href="/">
                  <Image src={logo} className="inline pl-5" alt="logo" />
                </Link>
                <ul className={'b mt-10 flex flex-col divide-y divide-gray-500/10'}>
                  {navigation?.links &&
                    navigation.links.map((item, idx) => (
                      <li key={`nav-login-${idx}`} className="group relative px-5 py-5">
                        <PrismicNextLink
                          field={item.link}
                          onClick={() => setOpen(false)}
                          className={cn(
                            'hover:text-primary text-base font-medium text-gray-500 transition-all',
                            (item.link as { url: string }).url === currentRoute && 'text-primary',
                          )}>
                          {asText(item.label)}
                        </PrismicNextLink>
                      </li>
                    ))}
                </ul>
              </div>
              <div>
                <div
                  className={
                    'flex flex-row items-center justify-center space-x-2.5 border-t border-gray-300 bg-gray-100/50 py-5'
                  }>
                  <Show when="signed-out">
                    <Link href="/sign-in" className={cn(buttonVariants({ variant: 'default' }))}>
                      Log in
                    </Link>
                  </Show>
                  <Show when="signed-out">
                    <Link href="/sign-up" className={buttonVariants({ variant: 'outline' })}>
                      Buy now
                    </Link>
                  </Show>
                  <Show when="signed-in">
                    <Link href="/refresh" className={buttonVariants({ variant: 'outline' })}>
                      Go to Course
                    </Link>
                    {isAdmin && (
                      <Link href="/admin" className={buttonVariants({ variant: 'outline' })}>
                        Admin
                      </Link>
                    )}
                  </Show>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}

export default NavLogin;
